import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Alert, Button } from 'react-bootstrap'
import {
  generateReceipt,
  getReceiptJobStatus,
  salesList
} from '../actions/posActions.js'
import { checkoutSale } from '../actions/inventoryActions.js'
import SearchPos from '../components/PosSearchBox.js'
import PaginatePos from '../components/PaginatePos.js'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import PosProductTable from '../components/pos/PosProductTable.js'
import PosPrintableReceipt from '../components/pos/PosPrintableReceipt.js'
import PosReceiptCard from '../components/pos/PosReceiptCard.js'
import { ThemeContext } from '../components/ThemeContext.js'
import { MULTI_INVENTORY_UPDATE_RESET } from '../constants/inventoryConstants.js'
import { GENERATE_RECEIPT_RESET } from '../constants/posConstants.js'
import { GET_SALE_ITEM_RESET } from '../constants/saleConstants.js'
import { trackEvent, toAnalyticsItem } from '../utils/analytics.js'
import {
  defaultItemInput,
  clampDiscount,
  formatCurrency,
  buildCartItem,
  computeTotals,
  buildReceiptPayload,
  buildReceiptFilename
} from '../utils/cart.js'

const ANALYTICS_CURRENCY = 'USD'
const RECEIPT_STATUS_POLL_INTERVAL_MS = 2000
const RECEIPT_STATUS_MAX_POLLS = 30

const Pointofsale = () => {
  const { theme } = useContext(ThemeContext)
  const [show, setShow] = useState(false)
  const [shipping, setShipping] = useState(0)
  const [qrid, setQrid] = useState('')
  const [receiptMethod, setReceiptMethod] = useState('')
  const [checkoutValidationError, setCheckoutValidationError] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [itemInputs, setItemInputs] = useState({})
  const [notice, setNotice] = useState(null)
  const [pendingPurchase, setPendingPurchase] = useState(null)
  const [receiptJobId, setReceiptJobId] = useState(null)
  const [receiptPollingEnabled, setReceiptPollingEnabled] = useState(false)
  const [printableSale, setPrintableSale] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleClose = () => setShow(false)

  const { pageNumberpos = 1, keyword = '' } = useParams()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const activeSession = ownerInfo || userInfo
  const bname = activeSession?.bname || activeSession?.name || ''
  const vfname = bname.toLowerCase().replace(/\s/g, '')

  const saleList = useSelector((state) => state.saleList)
  const { sales = [], loading, error, pagespos, pagepos, countpos } = saleList

  const settingList = useSelector((state) => state.settingList)
  const { settings = {} } = settingList

  const multiInventoryUpdate = useSelector(
    (state) => state.multiInventoryUpdate || {}
  )
  const {
    success: inventoryUpdateSuccess,
    error: inventoryUpdateError,
    sale: completedSale
  } = multiInventoryUpdate

  const {
    error: receiptError,
    success: receiptSuccess,
    genreceipt: receiptResult
  } = useSelector((state) => state.generateReceipt || {})

  const taxrate = Number(settings?.taxrate || 0)

  const totals = useMemo(
    () => computeTotals(cartItems, shipping, taxrate),
    [cartItems, shipping, taxrate]
  )

  const checkoutCart = useMemo(
    () => ({
      shipping,
      qrid: receiptMethod === 'electronic' ? qrid.trim() : '',
      items: cartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        discount: item.itemdiscount
      }))
    }),
    [cartItems, shipping, qrid, receiptMethod]
  )

  const handleShow = () => {
    setReceiptMethod('')
    setCheckoutValidationError('')

    if (cartItems.length > 0) {
      trackEvent('begin_checkout', {
        currency: ANALYTICS_CURRENCY,
        value: totals.total,
        items: cartItems.map(toAnalyticsItem)
      })
    }

    setShow(true)
  }

  const childToparent = (data) => {
    const nextShipping = Math.max(0, Number(data) || 0)
    setShipping(nextShipping)
  }

  const handleReceiptMethodChange = (method) => {
    setReceiptMethod(method)
    setCheckoutValidationError('')
  }

  const handleQridChange = (value) => {
    setQrid(value)
    setCheckoutValidationError('')
  }

  const handleItemInputChange = (id, field, value) => {
    setItemInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || defaultItemInput),
        [field]: field === 'quantity' ? value : clampDiscount(value)
      }
    }))
  }

  const deleteItem = (id) => {
    const removedItem = cartItems.find((item) => item.id === id)

    if (removedItem) {
      trackEvent('remove_from_cart', {
        currency: ANALYTICS_CURRENCY,
        value: removedItem.lineSubtotal,
        items: [toAnalyticsItem(removedItem)]
      })
    }

    setCartItems((prev) => prev.filter((item) => item.id !== id))
    setNotice(null)
  }

  const addToReceipt = (unit) => {
    if (cartItems.some((item) => item.id === unit._id)) {
      setNotice({
        variant: 'warning',
        message: 'Item already in receipt.'
      })
      return
    }

    const cartItem = buildCartItem(unit, itemInputs[unit._id])

    setCartItems((prev) => [...prev, cartItem])

    trackEvent('add_to_cart', {
      currency: ANALYTICS_CURRENCY,
      value: cartItem.lineSubtotal,
      items: [toAnalyticsItem(cartItem)]
    })

    setItemInputs((prev) => ({
      ...prev,
      [unit._id]: defaultItemInput
    }))

    setNotice(null)
  }

  const resetReceiptState = () => {
    setCartItems([])
    setItemInputs({})
    setShipping(0)
    setQrid('')
    setReceiptMethod('')
    setCheckoutValidationError('')
    setNotice(null)
  }

  const cancelRcpt = () => {
    if (cartItems.length === 0) {
      return
    }

    resetReceiptState()
    handleClose()
    navigate('/pos')
  }

  const checkOut = (e) => {
    e.preventDefault()

    if (!receiptMethod) {
      setCheckoutValidationError('Choose an electronic or paper receipt.')
      return
    }

    if (receiptMethod === 'electronic' && !qrid.trim()) {
      setCheckoutValidationError(
        'Enter or scan the customer QR code for an electronic receipt.'
      )
      return
    }

    if (cartItems.length === 0) {
      setNotice({
        variant: 'warning',
        message: 'Add at least one item before checkout.'
      })
      handleClose()
      return
    }

    setPendingPurchase({
      qrid: receiptMethod === 'electronic' ? qrid.trim() : '',
      receiptMethod,
      affiliation: bname || undefined,
      items: cartItems.map(toAnalyticsItem)
    })

    dispatch(checkoutSale(checkoutCart))

    handleClose()
    resetReceiptState()
    navigate('/pos')
  }

  useEffect(() => {
    dispatch(salesList(pageNumberpos, keyword))
  }, [dispatch, pageNumberpos, keyword])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateSuccess || !completedSale) {
      return
    }

    trackEvent('purchase', {
      transaction_id: completedSale.barcode,
      affiliation: pendingPurchase.affiliation,
      currency: ANALYTICS_CURRENCY,
      value: completedSale.total,
      tax: completedSale.taxes,
      shipping: completedSale.shipping,
      items: pendingPurchase.items
    })

    if (pendingPurchase.receiptMethod === 'paper') {
      setPrintableSale(completedSale)
      setNotice({
        variant: 'success',
        message: `Sale recorded. Opening the print dialog for transaction ${completedSale.barcode}.`
      })
    } else {
      dispatch(
        generateReceipt(buildReceiptPayload(completedSale), {
          qrid: pendingPurchase.qrid,
          qfname: buildReceiptFilename(completedSale.barcode, vfname),
          barcode: completedSale.barcode
        })
      )
      setNotice({
        variant: 'success',
        message: `Sale recorded. Transaction ${completedSale.barcode}`
      })
    }
    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
    dispatch(salesList(pageNumberpos, keyword))
  }, [
    dispatch,
    inventoryUpdateSuccess,
    completedSale,
    pendingPurchase,
    vfname,
    pageNumberpos,
    keyword
  ])

  useEffect(() => {
    if (!printableSale) {
      return undefined
    }

    const clearPrintableSale = () => setPrintableSale(null)
    window.addEventListener('afterprint', clearPrintableSale)
    window.print()

    return () => window.removeEventListener('afterprint', clearPrintableSale)
  }, [printableSale])

  useEffect(() => {
    if (!receiptSuccess) {
      return
    }

    const receiptStatus = receiptResult?.status
    const hasJobId =
      typeof receiptResult?.jobId === 'string' && receiptResult.jobId.length > 0
    const accepted = receiptStatus === 'accepted' && hasJobId
    const receiptMessage =
      accepted ?
        `${receiptResult.message || 'Receipt accepted for processing.'} Delivery is not yet confirmed.${receiptResult.jobId ? ` Job ID: ${receiptResult.jobId}.` : ''}`
      : receiptStatus === 'accepted' ?
        'Receipt was accepted for processing, but the API did not provide a job ID to check its status.'
      : receiptStatus === 'failed' ?
        receiptResult.message || 'Receipt delivery failed.'
      : receiptStatus ?
        `Receipt status: ${receiptStatus}${receiptResult.message ? ` — ${receiptResult.message}` : ''}`
      : 'The API did not provide a receipt-delivery status.'

    setNotice({
      variant: accepted ? 'info' : 'warning',
      message: `Sale recorded. ${receiptMessage}`
    })
    if (accepted) {
      setReceiptJobId(receiptResult.jobId)
      setReceiptPollingEnabled(true)
    }
    dispatch({ type: GENERATE_RECEIPT_RESET })
  }, [dispatch, receiptSuccess, receiptResult])

  useEffect(() => {
    if (!receiptJobId || !receiptPollingEnabled) {
      return undefined
    }

    let cancelled = false
    let timeoutId
    let pollCount = 0

    const pollReceiptStatus = async () => {
      pollCount += 1

      try {
        const status = await dispatch(getReceiptJobStatus(receiptJobId))
        if (cancelled) {
          return
        }

        if (status.status === 'completed') {
          setNotice({
            variant: 'success',
            message: `Sale recorded. Receipt processing completed. Job ID: ${receiptJobId}.`
          })
          setReceiptJobId(null)
          setReceiptPollingEnabled(false)
          return
        }

        if (status.status === 'failed') {
          setNotice({
            variant: 'warning',
            message: `Sale recorded, but receipt processing failed after retries. Job ID: ${receiptJobId}.`
          })
          setReceiptJobId(null)
          setReceiptPollingEnabled(false)
          return
        }

        setNotice({
          variant: 'info',
          message: `Sale recorded. Receipt is processing. Job ID: ${receiptJobId}.`
        })
      } catch (error) {
        if (cancelled) {
          return
        }

        const statusError =
          error.response?.data?.message ||
          error.message ||
          'Status check failed'
        setNotice({
          variant: 'warning',
          message: `Unable to check receipt status right now: ${statusError}. Retrying. Job ID: ${receiptJobId}.`
        })
      }

      if (pollCount >= RECEIPT_STATUS_MAX_POLLS) {
        setNotice({
          variant: 'warning',
          message: `Receipt status is still unconfirmed. Check again later. Job ID: ${receiptJobId}.`
        })
        setReceiptPollingEnabled(false)
        return
      }

      timeoutId = window.setTimeout(
        pollReceiptStatus,
        RECEIPT_STATUS_POLL_INTERVAL_MS
      )
    }

    timeoutId = window.setTimeout(
      pollReceiptStatus,
      RECEIPT_STATUS_POLL_INTERVAL_MS
    )

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [dispatch, receiptJobId, receiptPollingEnabled])

  useEffect(() => {
    if (!receiptError) {
      return
    }

    setNotice({
      variant: 'warning',
      message: `Sale recorded, but the receipt could not be sent: ${receiptError}`
    })
    dispatch({ type: GENERATE_RECEIPT_RESET })
  }, [dispatch, receiptError])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateError) {
      return
    }

    setNotice({
      variant: 'danger',
      message: `Sale was not recorded: ${inventoryUpdateError}`
    })
    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
    dispatch(salesList(pageNumberpos, keyword))
  }, [dispatch, inventoryUpdateError, pendingPurchase, pageNumberpos, keyword])

  useEffect(() => {
    if (!activeSession) {
      dispatch({ type: GET_SALE_ITEM_RESET })
      navigate('/login')
    }
  }, [dispatch, navigate, activeSession])

  return (
    <Container
      className='pos-terminal'
      style={{
        color: theme.text,
        '--pos-border': theme.surfaceMuted,
        '--pos-surface': theme.card,
        '--pos-text': theme.text,
        '--pos-muted': theme.subtext,
        '--pos-accent': theme.accent,
        '--pos-soft-accent': theme.surfaceMuted
      }}
    >
      <Row className='align-items-end my-3 pos-heading'>
        <Col>
          <h1 className='mb-1' style={{ color: theme.text }}>
            Sales terminal
          </h1>
          <p className='mb-0' style={{ color: theme.subtext }}>
            Find products, add them to the sale, and review the total.
          </p>
        </Col>
      </Row>
      <Row className='align-items-center my-3 pos-search-row'>
        <Col>
          <Routes>
            <Route path='/*' element={<SearchPos />} />
          </Routes>
        </Col>
      </Row>

      <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 pos-results-toolbar'>
        <h2 className='pos-section-title mb-0'>Products</h2>
        <span className='pos-results-count' style={{ color: theme.subtext }}>
          {countpos ?? 0} products
        </span>
      </div>
      <div className='pos-pagination'>
        <PaginatePos pagespos={pagespos} pagepos={pagepos} />
      </div>
      {notice && (
        <Alert
          variant={notice.variant}
          dismissible
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      )}
      {receiptJobId && !receiptPollingEnabled && (
        <Button
          type='button'
          variant='outline-primary'
          className='mb-3'
          onClick={() => setReceiptPollingEnabled(true)}
        >
          Check receipt status again
        </Button>
      )}
      {countpos === 0 && <Message variant='info'>None Found</Message>}
      {loading ?
        <Loader />
      : error ?
        <Message variant='danger'>{error}</Message>
      : <Row className='g-3 align-items-start pos-workspace'>
          <Col
            xs={12}
            lg={7}
            className='pos-catalog-panel'
            style={{ minWidth: 0 }}
          >
            <PosProductTable
              sales={sales}
              itemInputs={itemInputs}
              onInputChange={handleItemInputChange}
              onAdd={addToReceipt}
            />
          </Col>
          <Col
            xs={12}
            lg={5}
            className='pos-cart-column'
            style={{ minWidth: 0 }}
          >
            <PosReceiptCard
              theme={theme}
              cartItems={cartItems}
              totals={totals}
              taxrate={taxrate}
              onDelete={deleteItem}
              onShippingChange={childToparent}
              onCancelSale={cancelRcpt}
              showCheckout={show}
              onOpenCheckout={handleShow}
              onCloseCheckout={handleClose}
              onSaveCheckout={checkOut}
              receiptMethod={receiptMethod}
              onReceiptMethodChange={handleReceiptMethodChange}
              checkoutValidationError={checkoutValidationError}
              qrid={qrid}
              onQridChange={handleQridChange}
            />
          </Col>
        </Row>
      }
      {cartItems.length > 0 && (
        <div className='pos-mobile-cart-bar d-lg-none'>
          <div>
            <span>
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} ·
              Total
            </span>
            <strong>{formatCurrency(totals.total)}</strong>
          </div>
          <a className='btn btn-primary' href='#pos-current-sale'>
            Review sale
          </a>
        </div>
      )}
      {printableSale && (
        <PosPrintableReceipt sale={printableSale} businessName={bname} />
      )}
    </Container>
  )
}

export default Pointofsale
